import { useNavigate } from "react-router-dom"
import { Dialog, DialogPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { navigationRoutes } from "../constants/constants";
import { collectPayAsYouGoClick } from "../api/thunks/user_interactions";
import { useDispatch } from "react-redux";
import { createCheckoutSession, createGuestCheckoutSession } from "../api/thunks/users";
import { useAppDispatch, useAppSelector } from "../store/hooks";

export const PricingComponent = ({ showNav }: { showNav: boolean }) => {
    if (window.location.pathname === "/pricing") {
        document.title = "Pricing | PocketQuant";
    }
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.user);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <>
            {showNav && (
                <div className="inset-x-0 p-6 lg:px-8 top-0 z-50">
                    <div className="flex items-center justify-between" aria-label="Global">
                        <div className="flex lg:flex-1">
                            <a href="/" className="-m-1.5 p-1.5">
                                <span className="sr-only">PocketQuant</span>
                                <img
                                    className="h-16 md:h-20 rounded-lg w-auto"
                                    src="pq-icon-512.png"
                                    alt=""
                                />
                            </a>
                        </div>
                        <div className="hidden lg:flex lg:gap-x-12">
                            {navigationRoutes.map((item) => (
                                <a key={item.name} href={item.href} className="text-sm font-semibold leading-6 text-gray-900">
                                    {item.name}
                                </a>
                            ))}
                        </div>
                        <div className="flex lg:hidden">
                            <button
                                type="button"
                                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                                onClick={() => setMobileMenuOpen(true)}
                            >
                                <span className="sr-only">Open main menu</span>
                                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        {
                            window.location.pathname === '/pricing' && (
                                <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                                    <a href="/signup" className="text-sm font-semibold leading-6 text-gray-900">
                                        Sign Up <span aria-hidden="true">&rarr;</span>
                                    </a>
                                </div>
                            )
                        }
                    </div>
                    <Dialog className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
                        <div className="fixed inset-0 z-50" />
                        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                            <div className="flex items-center justify-between">
                                <a href="/" className="-m-1.5 p-1.5">
                                    <span className="sr-only">PocketQuant</span>
                                    <img
                                        className="h-16 md:h-20 rounded-lg w-auto"
                                        src="pq-icon-512.png"
                                        alt=""
                                    />
                                </a>
                                <button
                                    type="button"
                                    className="-m-2.5 rounded-md p-2.5 text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <span className="sr-only">Close menu</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div className="mt-6 flow-root">
                                <div className="-my-6 divide-y divide-gray-500/10">
                                    <div className="py-6">
                                        <a
                                            href="/signup"
                                            className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                                        >
                                            Sign Up
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </Dialog>
                </div>
            )}

            <div className="max-w-6xl mx-auto px-2 sm:px-4">
                {/* Header */}
                <div className="text-center mb-6">
                    <h1 className="text-3xl md:text-4xl font-bold mb-4">When time is money, pick PocketQuant</h1>
                </div>

                {/* Pricing Cards */}
                <div
                    className="
                        flex flex-col
                        md:flex-row
                        md:flex-wrap
                        justify-center
                        items-stretch
                        gap-6
                        w-full
                        max-w-full
                        overflow-x-auto
                        md:overflow-x-visible
                        pb-4
                    "
                >
                    {/* Pay As You Go Plan */}
                    <div className="relative border rounded-lg shadow-lg p-6 sm:p-8 w-full sm:w-96 md:w-80 bg-white flex flex-col justify-between
                        before:content-[''] before:absolute before:inset-0 before:-z-10 before:rounded-lg
                        before:bg-orange-400/40 before:blur-xl before:opacity-70 hover:before:opacity-100 transition-opacity"
                        style={{ boxShadow: '0 0 32px 8px rgba(255,140,0,0.25)' }}
                    >
                        <div>
                            <h2 className="text-xl font-semibold">Pay As You Go</h2>
                            <div className="mb-4">Research without limits</div>
                            <div className="mb-2 text-sm text-gray-500">
                                (25% profit margin)
                            </div>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> No monthly commitment</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> All Premium features</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Unlimited everything</li>
                            </ul>
                            <div className="mb-2 text-xs text-gray-500">
                                *$1 additional charge per billing cycle to cover Stripe fees. Only for periods with usages. You will not be billed $1 if there is no usage
                            </div>
                        </div>
                        <button onClick={async () => {
                            collectPayAsYouGoClick()

                            if (process.env.REACT_APP_PQ_PAY_AS_YOU_GO_PRICE_ID === undefined) {
                                alert("Failed to create checkout session 5.1");
                                return;
                            }

                            if (user.credentials?.accessToken === undefined) {
                                const res = await dispatch(
                                    createGuestCheckoutSession({
                                        productCode: process.env.REACT_APP_PQ_PAY_AS_YOU_GO_PRICE_ID,
                                    })
                                );
                                if (res.meta.requestStatus === "fulfilled") {
                                    window.location.href = (res.payload as any).redirectUrl;
                                } else {
                                    alert("Failed to create checkout session 5.2");
                                }

                            } else {
                                const res = await dispatch(
                                    createCheckoutSession({
                                        productCode: process.env.REACT_APP_PQ_PAY_AS_YOU_GO_PRICE_ID,
                                    })
                                );
                                if (res.meta.requestStatus === "fulfilled") {
                                    window.location.href = (res.payload as any).redirectUrl;
                                } else {
                                    alert("Failed to create checkout session 5.2");
                                }
                            }


                        }} className="w-full bg-orange-500 text-white py-2 rounded-lg mt-6">Get Started</button>
                    </div>

                    {/* Free Plan */}
                    {/* <div className="border rounded-lg shadow-lg p-6 sm:p-8 w-full sm:w-96 md:w-80 flex flex-col justify-between bg-white">
                        <div>
                            <h2 className="text-xl font-semibold">Free</h2>
                            <div className="mb-4">Powerful tools, for free</div>
                            <div className="text-4xl font-bold mb-4">$0<span className="text-sm font-normal">USD/month</span></div>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 10 copilot prompts</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 2nd Most recent earnings report</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 2nd Most recent transcript</li>
                            </ul>
                        </div>
                        <button onClick={() => navigate('/signup')} className="w-full bg-orange-500 text-white py-2 rounded-lg mt-6">Get Started</button>
                    </div> */}

                    {/* Premium Plan */}
                    <div className="border rounded-lg shadow-lg p-6 sm:p-8 w-full sm:w-96 md:w-80 bg-orange-50 flex flex-col justify-between">
                        <div>
                            <h2 className="text-xl font-semibold">Premium</h2>
                            <div className="mb-4">Faster, smarter investments</div>
                            <div className="text-4xl font-bold mb-4">$20<span className="text-sm font-normal">USD/month</span></div>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 250 monthly prompts</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Earnings reports since 2009</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Transcripts since 2021</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 10 Workflow executions per month</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Feature/Data source requests</li>
                            </ul>
                        </div>
                        <button onClick={async () => {
                            if (process.env.REACT_APP_PQ_PREMIUM_PRICE_ID === undefined) {
                                alert("Failed to create checkout session 5.3");
                                return;
                            }

                            if (user.credentials?.accessToken === undefined) {
                                const res = await dispatch(
                                    createGuestCheckoutSession({
                                        productCode: process.env.REACT_APP_PQ_PREMIUM_PRICE_ID,
                                    })
                                );
                                if (res.meta.requestStatus === "fulfilled") {
                                    window.location.href = (res.payload as any).redirectUrl;
                                } else {
                                    alert("Failed to create checkout session 5.4");
                                }

                            } else {
                                const res = await dispatch(
                                    createCheckoutSession({
                                        productCode: process.env.REACT_APP_PQ_PREMIUM_PRICE_ID,
                                    })
                                );
                                if (res.meta.requestStatus === "fulfilled") {
                                    window.location.href = (res.payload as any).redirectUrl;
                                } else {
                                    alert("Failed to create checkout session 5.5");
                                }
                            }
                        }} className="w-full bg-orange-500 text-white py-2 rounded-lg mt-6">Buy plan</button>
                    </div>

                    {/* Pro Plan */}
                    {/* <div className="border rounded-lg shadow-lg p-6 sm:p-8 w-full sm:w-96 md:w-80 bg-orange-50 flex flex-col justify-between">
                        <div>
                            <h2 className="text-xl font-semibold">Pro</h2>
                            <div className="mb-4">Automate investment research</div>
                            <div className="text-4xl font-bold mb-4">$49<span className="text-sm font-normal">USD/month</span></div>
                            <ul className="space-y-4 mb-6">
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 250 monthly prompts</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Access to advanced models (o3, 4.1...)</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Earnings reports since 2009</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Transcripts since 2021</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> 10 Workflow executions per month</li>
                                <li className="flex items-center"><span className="text-orange-500 mr-2">✓</span> Feature/Data source requests</li>
                            </ul>
                        </div>
                        <button onClick={() => navigate('/signup?redirect=checkout&price_id=' + process.env.REACT_APP_PQ_PRO_PRICE_ID)} className="w-full bg-orange-500 text-white py-2 rounded-lg mt-6">Buy plan</button>
                    </div> */}
                </div>
            </div>
        </>
    )
}
